/**
 * Mapbox geocoding adapter — 1:1 wrap of the historical fetch calls used
 * inline in both LocationPickers + `shared/geocode.ts`. No behavior change:
 * same URLs, same `country=PR` biasing, same `place_name` shape. The token
 * is resolved internally via `getMapboxToken()` so consumers don't pass it
 * around any more.
 *
 * See [[shared/maps/types.ts]] for the GeocodeConnector contract.
 */

import type { GeocodeConnector, ForwardGeocodeOptions } from '../../types';
import { getMapboxToken } from './token';

const MAPBOX_GEOCODE_BASE = 'https://api.mapbox.com/geocoding/v5/mapbox.places';

export const mapboxGeocodeConnector: GeocodeConnector = {
  async reverseGeocode(lat, lng) {
    const token = getMapboxToken();
    if (!token) return null;
    try {
      const res = await fetch(`${MAPBOX_GEOCODE_BASE}/${lng},${lat}.json?access_token=${token}`);
      if (!res.ok) return null;
      const data = await res.json();
      const feature = data?.features?.[0];
      if (!feature) return null;
      return {
        formatted: feature.place_name ?? `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
        placeName: typeof feature.text === 'string' ? feature.text : undefined,
      };
    } catch {
      return null;
    }
  },

  async reverseGeocodeMunicipality(lat, lng) {
    const token = getMapboxToken();
    if (!token) return null;
    try {
      const res = await fetch(
        `${MAPBOX_GEOCODE_BASE}/${lng},${lat}.json` +
          `?access_token=${token}&types=place&country=PR&limit=1`,
      );
      if (!res.ok) return null;
      const body = await res.json();
      const name: string | undefined = body?.features?.[0]?.text;
      return name || null;
    } catch {
      return null;
    }
  },

  async forwardGeocode(query, opts: ForwardGeocodeOptions = {}) {
    const token = getMapboxToken();
    if (!token) return null;
    const country = opts.country ?? 'PR';
    const types = opts.types ?? 'address,poi';
    try {
      const res = await fetch(
        `${MAPBOX_GEOCODE_BASE}/${encodeURIComponent(query)}.json` +
          `?access_token=${token}&country=${country}&types=${types}&limit=1`,
      );
      if (!res.ok) return null;
      const data = await res.json();
      const feature = data?.features?.[0];
      if (!feature) return null;
      const [lng, lat] = feature.center as [number, number];
      return {
        lat,
        lng,
        address: feature.place_name as string,
        placeId: typeof feature.id === 'string' ? feature.id : undefined,
      };
    } catch {
      return null;
    }
  },
};
